import { formatCurrency } from '@if/core/utils'
import { Banner, BodyText, HighlightCallout, InvoiceTable, SummaryCard } from '@if/mui'
import { Stack } from '@mui/material'
import { companyConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { EPayoutRecoveryMethod, MerchantId } from '@src/types/Constants'
import { FundingSummaryDto } from '@src/types/FundingSummaryDto'
import { Building2, CircleDollarSign } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import RetentionBreakdown from './RetentionBreakdown'
import { useFormatFundingDate } from './shared'

interface Props {
  fundingSummary: FundingSummaryDto
}

const FundingConfirmationCard = ({ fundingSummary }: Props) => {
  const { t } = useTranslation()
  const config = companyConfigByFinancingProgramId(fundingSummary.financingProgramId)
  const formatFundingDate = useFormatFundingDate()

  const payments = fundingSummary.merchantPayments
  const merchantFee = parseFloat(fundingSummary.merchantFee) || 0
  const hasMerchantFee = merchantFee > 0
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0)
  const netAmount = totalPayments - merchantFee
  const { productsFundingDetails } = fundingSummary
  const isProductsRetentionPayoutMode = productsFundingDetails?.payoutRecoveryMethod === EPayoutRecoveryMethod.Retention

  const isPayCustomer = (merchantId: string) =>
    merchantId === MerchantId.PayCustomerByBankTransfer.toString() ||
    merchantId === MerchantId.PayCustomerByCheque.toString()

  const getPaymentName = (payment: (typeof payments)[0]) => {
    if (isPayCustomer(payment.merchantId)) {
      return fundingSummary.customerNameProvince.split(' / ')[0]
    }
    return payment.merchantName ?? fundingSummary.merchantName
  }

  const getPaymentAmount = (payment: (typeof payments)[0]) => {
    if (productsFundingDetails?.payoutRecoveryMethod && payment.merchantId === fundingSummary.merchantId) {
      return productsFundingDetails.totalPayoutAmount
    }
    const amount = payment.amount ?? 0
    if (merchantFee > 0 && payment.merchantId === fundingSummary.merchantId) {
      return amount - merchantFee
    }
    return amount
  }

  const amountText = t(
    hasMerchantFee ? 'fundingSummary.amountConfirmationMerchantFee' : 'fundingSummary.amountConfirmation',
    {
      financingCompanyName: config.name,
      amount: formatCurrency(totalPayments),
      merchantFeeRate: fundingSummary.merchantFeeRate,
      date: formatFundingDate(fundingSummary.activationOn),
    },
  )

  const showPaymentsTable = !isProductsRetentionPayoutMode && payments.length > 1

  return (
    <SummaryCard title={t('fundingSummary.fundingConfirmation')} icon={CircleDollarSign}>
      <Stack spacing={2}>
        <BodyText>{amountText}</BodyText>

        {!isProductsRetentionPayoutMode && (
          <HighlightCallout
            icon={Building2}
            subjectLabel={t('common.merchant')}
            subject={fundingSummary.merchantName}
            valueLabel={t('fundingSummary.netAmount')}
            value={formatCurrency(netAmount)}
          />
        )}

        {showPaymentsTable && (
          <InvoiceTable
            columns={
              [{ label: t('common.merchant') }, { label: t('fundingSummary.amountCol'), type: 'currency' }] as const
            }
            items={payments.map((payment) => ({
              cells: [getPaymentName(payment), getPaymentAmount(payment)] as [string, number],
            }))}
          />
        )}

        {isProductsRetentionPayoutMode && productsFundingDetails && (
          <RetentionBreakdown productsFundingDetails={productsFundingDetails} />
        )}

        <Banner severity="warning">
          {t('fundingSummary.postDateDisbursement')}
          {productsFundingDetails?.payoutRecoveryMethod === EPayoutRecoveryMethod.Chargeback &&
            productsFundingDetails.chargebackEndDate &&
            ` ${t('fundingSummary.chargebackNotice', {
              date: formatFundingDate(productsFundingDetails.chargebackEndDate),
            })}`}
        </Banner>
      </Stack>
    </SummaryCard>
  )
}

export default FundingConfirmationCard
